import { Callback } from "game-common/models"
import { Container, Graphics, Text } from "pixi.js"
import { chipMetrics, controlHitRect, getUiMetrics, ResponsiveHitRect } from "./ui_metrics"
import { TextRenderer } from "../text/text_renderer"
import { blendColor, drawFlareGlow, FLARE_COLOR, FLARE_MS, flareIntensity, flareScale } from "./flare"

// The "you are not alone" chip: a small pill in the top-right corner, beside the map toggle, carrying
// the number of OTHER players logged into the server. It is present only while there is somebody else
// to count -- a solo camp has no use for it, and the corner is better empty than occupied by a zero.
//
// Deliberately built off the map chip's own plate (dark translucent, thin light rim, glyph + label) so
// the pair reads as one row of controls rather than two unrelated badges.
//
// Because every change to it happens unasked -- somebody else logged in, nothing the player did -- it
// announces itself with the HUD's shared flare (see ./flare) whenever the count ARRIVES OR GROWS: the
// plate goes hot, it pops against its own middle, and a glow with rings expands out of it, for three
// seconds. A count that drops is not news and passes in silence.
const GLYPH = "👥"

const PANEL_COLOR = 0x05070a
const PANEL_ALPHA = 0.59
const PANEL_ALPHA_HOVER = 0.82
const BORDER_ALPHA = 0.36
const BORDER_ALPHA_HOVER = 0.75
const LABEL_COLOR = 0xcfd8dc
const LABEL_COLOR_HOVER = 0xffffff

export class EmberPlayersChip extends Container {
    // Everything visible hangs off `body` rather than off the chip itself, so the flare's pop can
    // scale it about its own middle while the chip's x/y -- what layoutChipRow sets, and what the
    // players panel stacks beneath -- stay exactly where they were put.
    private metrics = chipMetrics()
    private metricSource = getUiMetrics()
    private body = new Container()
    private glow = new Graphics()
    private plate = new Graphics()
    private glyph: Text
    private label: Text
    private count = 0
    private hovered = false
    private flareMs = 0

    constructor(onClick: Callback) {
        super()

        // No tint on the emoji -- it carries its own colours.
        this.glyph = new TextRenderer().fontSize(this.metrics.glyphSize).render(GLYPH) as Text
        this.label = new TextRenderer().fontSize(this.metrics.labelSize).render("0") as Text
        this.body.addChild(this.glow, this.plate, this.glyph, this.label)
        this.addChild(this.body)
        this.layout()
        this.draw()

        this.interactive = true
        this.cursor = "pointer"
        this.on("mouseover", () => {
            this.hovered = true
            this.draw()
        })
        this.on("mouseout", () => {
            this.hovered = false
            this.draw()
        })
        this.on("click", onClick)
        this.on("tap", onClick)

        this.visible = false
        this.exposeDevTriggers()
    }

    // Browser-console triggers, in the same shape as window.rosterWidget / window.playerRing:
    //
    //     playersFlare()     // flare on the count that is up (or on a 1, if the chip is away)
    //     playersFlare(4)    // ...as if a fourth player had just joined
    //     playersChip        // the chip itself: isFlashing(), getWidth()
    //
    // Nothing a developer alone at a keyboard can do makes this chip move -- it takes a second player
    // logging into the server -- so there would otherwise be no way to look at the one thing it exists
    // to do, let alone tune it. The count it pretends to carry is corrected by the next players
    // broadcast a couple of seconds later.
    private exposeDevTriggers = () => {
        if (typeof window === "undefined") {
            return
        }
        ;(window as any).playersChip = this
        ;(window as any).playersFlare = (count?: number): string => {
            this.update(Math.max(1, Math.floor(count ?? this.count ?? 1)))
            this.flash()
            this.draw()
            return `[playersFlare] ${this.count} online, ${FLARE_MS}ms`
        }
    }

    // How many others there are. Any count that GROWS flares: the chip appearing out of nothing, a
    // second player joining the three already online, or walking into a camp while somebody else is
    // logged in. All of them are somebody arriving, which is the thing worth looking up for. A count
    // that falls just redraws -- a player leaving is not an event the player can act on, and flaring
    // on every change would make the corner a blinking light.
    update = (count: number) => {
        const next = Math.max(0, Math.floor(count || 0))
        const arrival = next > this.count
        this.count = next
        this.label.text = String(next)
        this.layout()
        this.visible = next > 0
        if (arrival) {
            this.flash()
        }
        this.draw()
    }

    flash = () => {
        this.flareMs = FLARE_MS
    }

    isFlashing = (): boolean => this.flareMs > 0

    // Drain the flare. Only repaints while one is running, so a chip that has settled costs nothing
    // per frame.
    animate = (dtMs: number) => {
        if (this.flareMs <= 0) {
            return
        }
        this.flareMs = Math.max(0, this.flareMs - dtMs)
        this.draw()
    }

    // Both sit on a shared centre line, so the glyph and the count don't stagger. Re-run whenever the
    // count's width changes (1 digit to 2), which also moves the hit area.
    private layout = () => {
        this.glyph.x = this.metrics.padX
        this.label.x = this.metrics.padX + this.glyph.width + this.metrics.gap
        const h = this.getHeight() - this.metrics.padY * 2
        this.glyph.y = this.metrics.padY + (h - this.glyph.height) / 2
        this.label.y = this.metrics.padY + (h - this.label.height) / 2
        // Pivot and position on the same point: the body then occupies its old box at rest and swells
        // symmetrically out of the middle when the pop scales it.
        const centre = { x: this.getWidth() / 2, y: this.getHeight() / 2 }
        this.body.pivot.set(centre.x, centre.y)
        this.body.position.set(centre.x, centre.y)
        // An explicit hit area a couple of px proud of the plate: the chip is small, and it is the one
        // way into the player list. Measured off the resting size, so a pop doesn't move the target
        // under a thumb that is already reaching for it.
        this.hitArea = new ResponsiveHitRect(() => {
            const rect = controlHitRect(this, this.getWidth(), this.getHeight(), this.metrics.hitPad)
            if (!getUiMetrics().touch) rect.pad(this.metrics.hitPad)
            return rect
        })
    }

    private draw = () => {
        const hot = flareIntensity(this.flareMs)
        const w = this.getWidth()
        const h = this.getHeight()
        // The row sits 6px off the top of the screen, so the halo is kept short: anything taller is
        // spent off-canvas.
        drawFlareGlow(this.glow, { remainingMs: this.flareMs, width: w, height: h, corner: this.metrics.corner })
        this.plate.clear()
        this.plate.beginFill(
            blendColor(PANEL_COLOR, FLARE_COLOR, hot * 0.85),
            (this.hovered ? PANEL_ALPHA_HOVER : PANEL_ALPHA) + hot * (1 - PANEL_ALPHA),
        )
        // The rim stays near white at the peak rather than going orange with the fill: a bright edge
        // around a hot plate is what makes the thing look lit instead of merely recoloured.
        this.plate.lineStyle(
            1 + hot * 1.5,
            blendColor(0xffffff, FLARE_COLOR, hot * 0.4),
            Math.min(1, (this.hovered ? BORDER_ALPHA_HOVER : BORDER_ALPHA) + hot),
        )
        this.plate.drawRoundedRect(0, 0, w, h, this.metrics.corner)
        this.plate.endFill()
        this.label.tint = blendColor(this.hovered ? LABEL_COLOR_HOVER : LABEL_COLOR, 0xffffff, hot)
        this.body.scale.set(flareScale(this.flareMs))
    }

    // Measured off the content rather than getBounds(), so the plate can be drawn to the size the
    // caller is about to position against without a chicken-and-egg on bounds -- and so the row's
    // layout is the chip's RESTING size, unmoved by a flare in progress.
    private refreshMetrics = () => {
        if (this.metricSource === getUiMetrics()) return
        this.metricSource = getUiMetrics()
        this.metrics = chipMetrics()
        this.glyph.style.fontSize = this.metrics.glyphSize
        this.label.style.fontSize = this.metrics.labelSize
        this.layout()
        this.draw()
    }

    getWidth = (): number => {
        this.refreshMetrics()
        return Math.max(this.metrics.minWidth, this.metrics.padX * 2 + this.glyph.width + this.metrics.gap + this.label.width)
    }
    getHeight = (): number => {
        this.refreshMetrics()
        return Math.max(this.metrics.minHeight, this.metrics.padY * 2 + Math.max(this.glyph.height, this.label.height))
    }
}
